import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { clearedReplacements, getBlackoutReplacements, replaceBlackoutResources } from "store/blackouts"
import { normalizeImpact } from "./normalizeImpact"

/** A stable empty fallback: a fresh `[]` from the selector would re-render on every action. */
const NO_ITEMS = []

/**
 * `errors: [{ booking_id, reason }]` from a rejected save -> `{ [booking_id]: reason }`.
 *
 * The reasons are per booking, not per resource, so a booking that lost two resources shows the
 * same reason on both of its rows — the reason itself names which one failed.
 */
const toBookingErrors = (error) =>
  (Array.isArray(error?.data?.errors) ? error.data.errors : []).reduce((errorsByBooking, rowError) => {
    if (rowError?.booking_id && rowError?.reason) errorsByBooking[rowError.booking_id] = rowError.reason
    return errorsByBooking
  }, {})

/**
 * The Replace Crew/Equipment sheet's data and selection.
 *
 * Kept out of the component because the selection has one rule worth stating: an assignment is
 * only ever `rowId -> candidateId`, one per stranded resource, so a guide can hand three stranded
 * bookings to three different people. Nothing is sent for a row left unassigned — the server must
 * not read a missing key as "unassign", which is why the payload filters rather than sending nulls.
 */
export const useReplaceResources = ({ blackoutId, scope, isOpen }) => {
  const dispatch = useDispatch()
  const [assignments, setAssignments] = useState({})
  const [bookingErrors, setBookingErrors] = useState({})

  const replacements = useSelector((state) => state.blackouts?.replacements)
  const isSaving = useSelector((state) => Boolean(state.blackouts?.replaceResources?.loading))

  const isLoading = Boolean(replacements?.loading)
  const hasFailed = Boolean(replacements?.failed)

  const impact = useMemo(
    () => (replacements?.data ? normalizeImpact(replacements.data, scope) : null),
    [replacements?.data, scope]
  )
  const rows = impact?.rows || NO_ITEMS

  /**
   * Refetched on every open rather than cached: a candidate free ten minutes ago may have been
   * booked since, and offering a stale one would fail on submit with nothing on screen to explain it.
   * The same refetch backs the Reload action after a save is rejected as stale.
   */
  const reload = useCallback(() => {
    setAssignments({})
    setBookingErrors({})
    return dispatch(getBlackoutReplacements(blackoutId)).catch(() => {})
  }, [dispatch, blackoutId])

  useEffect(() => {
    if (!isOpen || !blackoutId) return undefined
    reload()
    return () => dispatch(clearedReplacements())
  }, [dispatch, isOpen, blackoutId, reload])

  const assignOne = useCallback((row, candidateId) => {
    setAssignments((previous) => {
      const next = { ...previous }
      // antd clears a Select to `undefined`, which has to mean "leave this resource alone".
      if (candidateId === undefined || candidateId === null) delete next[row.id]
      else next[row.id] = candidateId
      return next
    })
    // A new pick answers the reason shown for that booking, so the reason goes with it.
    setBookingErrors((previous) => {
      if (!previous[row.booking_id]) return previous
      const next = { ...previous }
      delete next[row.booking_id]
      return next
    })
  }, [])

  const assignedCount = Object.keys(assignments).length

  /**
   * The save is all-or-nothing on the server: a rejection means nothing was written, so the picks
   * stay on screen and only the rows it names are marked.
   */
  const submit = useCallback(() => {
    const payload = {
      assignments: rows
        .filter((row) => assignments[row.id])
        .map((row) => ({
          booking_id: row.booking_id,
          from_resource_id: row.resource?.id,
          to_resource_id: assignments[row.id]
        }))
    }
    setBookingErrors({})
    return dispatch(replaceBlackoutResources(blackoutId, payload)).catch((error) => {
      setBookingErrors(toBookingErrors(error))
      throw error
    })
  }, [dispatch, blackoutId, rows, assignments])

  const hasBookingErrors = Object.keys(bookingErrors).length > 0

  return {
    rows,
    isLoading,
    hasFailed,
    isSaving,
    assignments,
    assignedCount,
    bookingErrors,
    hasBookingErrors,
    assignOne,
    submit,
    reload
  }
}
